import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ChevronRight, ShoppingBag, Check, MessageCircle, ArrowRight } from "lucide-react";
import { Toaster, toast } from "sonner";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import QuoteDialog from "../components/QuoteDialog";
import CartDrawer from "../components/CartDrawer";
import SEO from "../components/SEO";
import { ProductVisual } from "../components/ProductVisual";
import { ProductPhotos } from "../components/ProductCard";
import { useCatalog } from "../hooks/useCatalog";
import { useCart } from "../context/CartContext";
import { slugify, categorySeoByLabel, productPath } from "../data/seoPages";
import { buildWhatsappUrl } from "../constants/site";

const ProductPage = () => {
    const { slug } = useParams();
    const [quoteOpen, setQuoteOpen] = useState(false);
    const [added, setAdded] = useState(false);
    const { data: catalogData, isLoading } = useCatalog();
    const { addItem, setOpen } = useCart();

    const products = useMemo(() => catalogData?.items || [], [catalogData]);
    const product = products.find((p) => slugify(p.id) === slug);
    const categorySeo = product ? categorySeoByLabel[product.category] : null;

    const related = useMemo(() => {
        if (!product) return [];
        return products
            .filter((p) => p.category === product.category && p.id !== product.id)
            .slice(0, 3);
    }, [products, product]);

    const handleAdd = () => {
        if (!product) return;
        addItem(product, 1);
        setAdded(true);
        toast.success(`${product.name} ajouté au panier`, {
            action: { label: "Voir panier", onClick: () => setOpen(true) },
        });
        setTimeout(() => setAdded(false), 1400);
    };

    const photo = product?.photos?.[0] || product?.photo || null;

    return (
        <div className="App bg-[#FBFBFD] text-neutral-900 antialiased overflow-x-hidden">
            {product && (
                <SEO
                    title={`${product.name} au Maroc | DENTIPRIME`}
                    description={`${product.name} — ${product.tagline}. Disponible au Maroc chez DENTIPRIME, fournisseur de matériel dentaire professionnel. Livraison nationale 24h-72h.`}
                    url={`https://dentiprime.ma${productPath(product)}`}
                />
            )}
            <Navbar onQuoteClick={() => setQuoteOpen(true)} />

            <main className="pt-[68px]">
                {isLoading && (
                    <div className="flex justify-center py-32">
                        <Loader2 className="w-8 h-8 animate-spin text-[#0a84ff]" />
                    </div>
                )}

                {!isLoading && !product && (
                    <section className="py-32 text-center">
                        <h1 className="font-display text-4xl font-light text-neutral-900">
                            Produit introuvable
                        </h1>
                        <p className="mt-4 text-neutral-500">
                            Ce produit n'est plus au catalogue.
                        </p>
                        <Link
                            to="/"
                            className="mt-8 inline-flex items-center gap-2 text-[#0a84ff] font-medium hover:underline"
                        >
                            Retour au catalogue
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                    </section>
                )}

                {!isLoading && product && (
                    <>
                        <section className="py-14 md:py-20">
                            <div className="max-w-[1320px] mx-auto px-6 md:px-10">
                                <nav aria-label="Fil d'Ariane" className="flex flex-wrap items-center gap-1.5 text-[13px] text-neutral-500">
                                    <Link to="/" className="hover:text-[#0a84ff] transition-colors">
                                        Accueil
                                    </Link>
                                    <ChevronRight className="w-3.5 h-3.5" />
                                    {categorySeo ? (
                                        <Link to={categorySeo.path} className="hover:text-[#0a84ff] transition-colors">
                                            {product.category}
                                        </Link>
                                    ) : (
                                        <span>{product.category}</span>
                                    )}
                                    <ChevronRight className="w-3.5 h-3.5" />
                                    <span className="text-neutral-900 font-medium">{product.name}</span>
                                </nav>

                                <div className="mt-10 grid lg:grid-cols-2 gap-12 items-start">
                                    <div className="group relative aspect-[4/3] rounded-3xl bg-gradient-to-br from-[#F5F5F7] to-white border border-black/[0.05] overflow-hidden">
                                        <div
                                            className="absolute -bottom-10 -right-10 w-64 h-64 rounded-full blur-3xl opacity-50 pointer-events-none"
                                            style={{ background: product.accent }}
                                        />
                                        <div className="relative w-full h-full p-6 flex items-center justify-center">
                                            <ProductPhotos
                                                photos={product.photos && product.photos.length ? product.photos : product.photo ? [product.photo] : []}
                                                name={`${product.name} — ${product.category} au Maroc`}
                                                kind={product.kind}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <div className="dp-eyebrow">{product.category}</div>
                                        <h1 className="mt-3 font-display text-4xl md:text-5xl font-light tracking-tight text-neutral-900 leading-[1.08]">
                                            {product.name}
                                        </h1>
                                        <p className="mt-4 text-[16px] md:text-[17px] text-neutral-500 leading-relaxed">
                                            {product.tagline}
                                        </p>

                                        <ul className="mt-6 space-y-2 text-[13.5px] text-neutral-600 font-mono-tech">
                                            {(product.specs || []).map((s) => (
                                                <li key={s} className="flex items-center gap-2.5">
                                                    <span
                                                        className="w-1.5 h-1.5 rounded-full shrink-0"
                                                        style={{ background: product.accent }}
                                                    />
                                                    {s}
                                                </li>
                                            ))}
                                        </ul>

                                        <div className="mt-8 flex items-center gap-3">
                                            {product.original_price && (
                                                <span className="text-[14px] text-neutral-400 line-through tabular-nums">
                                                    {product.original_price} DH / د.م.
                                                </span>
                                            )}
                                            <span className="font-display text-3xl font-medium text-neutral-900 tabular-nums">
                                                {product.price_from && product.price_from !== "Sur devis"
                                                    ? `${product.price_from} DH / د.م.`
                                                    : "Sur devis"}
                                            </span>
                                        </div>

                                        <div className="mt-7 flex flex-wrap items-center gap-3">
                                            <button
                                                type="button"
                                                onClick={handleAdd}
                                                className={`dp-btn text-[13.5px] py-3 px-6 ${
                                                    added ? "bg-emerald-600 text-white" : "dp-btn-primary"
                                                }`}
                                            >
                                                {added ? (
                                                    <>
                                                        <Check className="w-4 h-4" />
                                                        Ajouté au panier
                                                    </>
                                                ) : (
                                                    <>
                                                        <ShoppingBag className="w-4 h-4" />
                                                        Ajouter au panier
                                                    </>
                                                )}
                                            </button>
                                            <a
                                                href={buildWhatsappUrl(
                                                    `Bonjour DENTIPRIME, je souhaite un devis pour : ${product.name}`
                                                )}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="dp-btn dp-btn-ghost text-[13.5px] py-3 px-6"
                                            >
                                                <MessageCircle className="w-4 h-4" />
                                                Demander un devis WhatsApp
                                            </a>
                                        </div>

                                        <p className="mt-6 text-[13px] text-neutral-400">
                                            Livraison nationale 24h-72h · Stock vérifié quotidiennement ·
                                            Support technique francophone
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {related.length > 0 && (
                            <section className="pb-24">
                                <div className="max-w-[1320px] mx-auto px-6 md:px-10">
                                    <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight text-neutral-900">
                                        Dans la même catégorie
                                    </h2>
                                    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {related.map((r) => (
                                            <Link
                                                key={r.id}
                                                to={productPath(r)}
                                                className="group rounded-2xl border border-black/[0.06] bg-white p-5 hover:-translate-y-0.5 hover:shadow-[0_20px_60px_-30px_rgba(10,132,255,0.35)] transition-all"
                                            >
                                                <div className="font-display text-[16px] font-medium text-neutral-900 group-hover:text-[#0a84ff] transition-colors">
                                                    {r.name}
                                                </div>
                                                <div className="mt-1 text-[13px] text-neutral-500">
                                                    {r.tagline}
                                                </div>
                                                <div className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[#0a84ff]">
                                                    Voir le produit
                                                    <ArrowRight className="w-3.5 h-3.5" />
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            </section>
                        )}
                    </>
                )}
            </main>

            <Footer />
            <QuoteDialog
                open={quoteOpen}
                onOpenChange={setQuoteOpen}
                defaultProduct={product?.name || ""}
            />
            <CartDrawer />
            <Toaster richColors position="bottom-right" />
        </div>
    );
};

export default ProductPage;
